import React, { useEffect, useState } from "react";
import WeightBandChart from "../general/WeightBandChart";
import BodyCircleChart from "../general/BodyCircleChart";
import {
  fetchBeefsByDate,
  fetchLambsByDate,
  fetchPigsByDate,
  fetchGoatsByDate,   
  fetchOthersByDate, 
} from "../../api/scheduling";
import { fetchSpecies } from "../../api/species";
import { Col, Form, Row } from "react-bootstrap";
import {
  bovineDisplayObj,
  ovineDisplayObj,
  porcineDisplayObj,
  caprineDisplayObj,  
  otherDisplayObj,    
} from "../../constants";
import "../../assets/css/scheduling.css";
import { CustomDatePicker } from "../general/datepicker";
import moment from "moment";
import CustomTable from "../general/CustomTable";
import { useCompany } from "../../helpers/CompanyContext";
import ProcessMonitorDrawer from "./ProcessMonitorDrawer";

// Species only available in BVM schema
const BVM_ONLY_SPECIES = ["Caprine", "Other"];
const BVM_COMPANY_ID = 2;

export default function ProcessMonitor() {
  const { selectedCompany } = useCompany();
  console.log("selectedCompany:", selectedCompany, typeof selectedCompany); 
  const [species, setSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState("Bovine");
  const [chartData, setChartData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedBody, setSelectedBody] = useState(null); // drawer state

  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [matchMethods, setMatchMethods] = useState({
    Bovine: {
      fetch: fetchBeefsByDate,
      displayItems: bovineDisplayObj,
      value: null,
      weightBandData: null,
      bodiesData: null,
    },
    Ovine: {
      fetch: fetchLambsByDate,
      displayItems: ovineDisplayObj,
      value: null,
      weightBandData: null,
      bodiesData: null,
    },
    Porcine: {
      fetch: fetchPigsByDate,
      displayItems: porcineDisplayObj,
      value: null,
      weightBandData: null,
      bodiesData: null,
    },
    Caprine: {
      fetch: fetchGoatsByDate,
      displayItems: caprineDisplayObj,
      value: null,
      weightBandData: null,
      bodiesData: null,
    },
    Other: {
      fetch: fetchOthersByDate,
      displayItems: otherDisplayObj,
      value: null,
      weightBandData: null,
      bodiesData: null,
    },
  });

  // Initialize the weight bands array
  const wdata = [
    { weightBand: "0-60", bodyCount: 0 },
    { weightBand: "61-80", bodyCount: 0 },
    { weightBand: "81-100", bodyCount: 0 },
    { weightBand: "101-120", bodyCount: 0 },
    { weightBand: "121-150", bodyCount: 0 },
    { weightBand: "> 151", bodyCount: 0 },
  ];

  const setWeightBandData = (objects) => {
    objects.forEach((obj) => {
      const hscw = obj.hscw || 0;
      if (hscw >= 0 && hscw <= 60) wdata[0].bodyCount++;
      else if (hscw >= 61 && hscw <= 80) wdata[1].bodyCount++;
      else if (hscw >= 81 && hscw <= 100) wdata[2].bodyCount++;
      else if (hscw >= 101 && hscw <= 120) wdata[3].bodyCount++;
      else if (hscw >= 121 && hscw <= 150) wdata[4].bodyCount++;
      else if (hscw > 150) wdata[5].bodyCount++;
    });
    return wdata;
  };

  const setBodiesData = (data) => {
    return data.reduce(
      (acc, obj) => {
        if (obj.grade_time) acc[0].value += 1;
        else acc[1].value += 1;
        return acc;
      },
      [
        { name: "Processed", value: 0 },
        { name: "Remaining", value: 0 },
      ]
    );
  };

  const handleDateChange = (date) => {
    let formatted_date = moment(date).format("YYYY-MM-DD");
    setSelectedDate(formatted_date);

    if (!matchMethods[selectedSpecies]?.fetch) {
      setItems([]);
      setGraphData([]);
      setChartData([]);
      return;
    }

    matchMethods[selectedSpecies]["fetch"](formatted_date, selectedCompany)
      .then((res) => {
        const sorted_items = res.sort((a, b) => a.body_number - b.body_number);
        setItems(sorted_items);
        let weightBandArr = setWeightBandData(sorted_items);
        const bodiesDataArr = setBodiesData(sorted_items);
        setGraphData(weightBandArr);
        setChartData(bodiesDataArr);
        setMatchMethods((prevState) => ({
          ...prevState,
          [selectedSpecies]: {
            ...prevState[selectedSpecies],
            value: sorted_items,
            weightBandData: weightBandArr,
            bodiesData: bodiesDataArr,
          },
        }));
      })
      .catch((err) => {
        console.log("error in fetching bodies", err);
      });
  };

  useEffect(() => {
    fetchSpecies()
      .then((data) => {
        setSpecies([...data]);    
      })
      .catch((error) => {
        console.error("Error fetching species : ", error);
      });

    fetchBeefsByDate(moment().format("YYYY-MM-DD"), selectedCompany)
      .then((res) => {
        const sorted_items = res.sort((a, b) => a.body_number - b.body_number);
        setItems(sorted_items);
        const weightBandArr = setWeightBandData(sorted_items);
        const bodiesDataArr = setBodiesData(sorted_items);
        setMatchMethods((prevState) => ({
          ...prevState,
          Bovine: {
            ...prevState.Bovine,
            value: sorted_items,
            weightBandData: weightBandArr,
            bodiesData: bodiesDataArr,
          },
        }));
        setGraphData(weightBandArr);
        setChartData(bodiesDataArr);
      })
      .catch((err) => {
        console.log("error in fetching beefs", err);
      });
  }, [selectedCompany]);

  const handleSpeciesChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedSpecies(selectedValue);

    if (!matchMethods[selectedValue]?.fetch) {
      setItems([]);
      setGraphData([]);
      setChartData([]);
      return;
    }

    if (
      !matchMethods[selectedValue]["value"] ||
      moment(matchMethods[selectedValue]["value"][0]?.production_date).format(
        "YYYY-MM-DD"
      ) !== selectedDate
    ) {
      matchMethods[selectedValue]["fetch"](selectedDate, selectedCompany)
        .then((res) => {
          const sorted_items = res.sort(
            (a, b) => a.body_number - b.body_number
          );
          setItems(sorted_items);
          let weightBandArr = setWeightBandData(sorted_items);
          const bodiesDataArr = setBodiesData(sorted_items);
          setGraphData(weightBandArr);
          setChartData(bodiesDataArr);
          setMatchMethods((prevState) => ({
            ...prevState,
            [selectedValue]: {
              ...prevState[selectedValue],
              value: sorted_items,
              weightBandData: weightBandArr,
              bodiesData: bodiesDataArr,
            },
          }));
        })
        .catch((err) => {
          console.log("error in fetching bodies", err);
        });
    } else {
      setItems(matchMethods[selectedValue]["value"]);
      setGraphData(matchMethods[selectedValue]["weightBandData"]);
      setChartData(matchMethods[selectedValue]["bodiesData"]);
    }
  };

  const [visibleColumns, setVisibleColumns] = useState(
    (matchMethods[selectedSpecies]?.displayItems || []).reduce((acc, item) => {
      acc[item.name] = true;
      return acc;
    }, {})
  );

  return (
    <div
      className="scheduling-page d-flex flex-column pt-2"
      style={{ fontSize: "14px" }}>
      <h4 className="mb-3 text-center text-acumen-color mt-1 pt-0">
        Process Monitor
      </h4>
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <Form.Group className="top-row">
            <Form.Label className="">Select Date</Form.Label>
            <div className="w-100 date-picker">
              <CustomDatePicker
                selectedDate={selectedDate}
                handleClearDate={null}
                handleDateChange={handleDateChange}
                inputLabel=""
                max_width="90%"
                maxDate={new Date()}
              />
            </div>
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group className="top-row">
            <Form.Label className="">Select Species</Form.Label>
            <Form.Select
              onChange={handleSpeciesChange}
              value={selectedSpecies}
              id="species"
              name="species"
              className="w-100 form-control">
              {species.length > 0 &&
                species
                  .filter((s) => s.scientific_name && s.scientific_name.trim() !== "")
                  .filter((s) =>
                    BVM_ONLY_SPECIES.includes(s.scientific_name)
                      ? selectedCompany === BVM_COMPANY_ID
                      : true
                  )
                  .map((s) => (
                    <option key={s.id} value={s.scientific_name}>
                      {s.scientific_name}
                    </option>
                  ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <hr />
      <div className="d-flex justify-content-around align-items-center">
        <div>
          <BodyCircleChart
            chartData={chartData}
            speciesName={selectedSpecies}
          />
        </div>
        <div>
          <WeightBandChart graphData={graphData} />
        </div>
      </div>

      <CustomTable
        items={items}
        columns={matchMethods[selectedSpecies]?.displayItems || []}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        onRowClick={(row) => setSelectedBody({ ...row, species: selectedSpecies })}
      />

      <ProcessMonitorDrawer
        open={!!selectedBody}
        onClose={() => setSelectedBody(null)}
        body={selectedBody}
        companyId={selectedCompany}
      />
    </div>
  );
}